import { TextField, Select, MenuItem, Autocomplete, Box } from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs from "dayjs";
import PropTypes from "prop-types";

import { getMappedColumns } from "@/components/membership/Template/utils/templateUtils";
import { useUpdateDataContext } from "@/contexts/UpdateDataContext";
import "@/components/membership/Template/TemplateInput.css";

export const OperatorSelector = ({ inputIndex, handleChange, value }) => {
  const operators = ["==", "!=", " >", "<", ">=", "<=", "in"];
  return (
    <Select
      value={value || null}
      onChange={(event) => handleChange(event.target.value, inputIndex)}
    >
      {operators.map((operator, index) => (
        <MenuItem key={index} value={operator}>
          {operator}
        </MenuItem>
      ))}
    </Select>
  );
};
OperatorSelector.propTypes = {
  inputIndex: PropTypes.number,
  handleChange: PropTypes.func,
  value: PropTypes.string,
};

export const NumberInput = ({ inputIndex, handleChange, value }) => {
  return (
    <TextField
      size="small"
      step="1"
      inputProps={{ type: "number" }}
      value={value || null}
      onChange={(event) => handleChange(event.target.value, inputIndex)}
    />
  );
};
NumberInput.propTypes = {
  inputIndex: PropTypes.number,
  handleChange: PropTypes.func,
  value: PropTypes.string,
};

export const DateInput = ({ inputIndex, handleChange, value }) => {
  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <DatePicker
        value={dayjs(value) || null}
        onChange={(newValue) =>
          handleChange(newValue.toISOString(), inputIndex)
        }
        sx={{
          ".MuiInputBase-root": {
            height: "2.5em",
          },
        }}
      />
    </LocalizationProvider>
  );
};
DateInput.propTypes = {
  inputIndex: PropTypes.number,
  handleChange: PropTypes.func,
  value: PropTypes.string,
};

export const TextInput = ({ inputIndex, handleChange, value, disabled }) => {
  // Using an Autocomplete component with an empty options array here instead of TextField, because I need the onChange event to fire when the component loses focuses instead of on every character change. MUI TextField doesn't support that option, but Autocomplete does this automatically.
  return (
    <Autocomplete
      autoSelect
      clearOnBlur
      value={value || null}
      onChange={(event, newValue) => handleChange(newValue, inputIndex)}
      freeSolo
      disablePortal
      options={[]}
      size="small"
      sx={{ width: 300 }}
      renderInput={(params) => <TextField {...params} />}
      disabled={disabled}
    />
  );
};
TextInput.propTypes = {
  inputIndex: PropTypes.number,
  handleChange: PropTypes.func,
  value: PropTypes.string,
};

export const ColumnSelector = ({ inputIndex, handleChange, value }) => {
  const { affiliateFileData, politicalData, templateData, workflow } =
    useUpdateDataContext();
  const mappedColumns = getMappedColumns(templateData.template);
  const columnNames =
    workflow === "political"
      ? politicalData.columns
      : affiliateFileData.columns;

  return (
    <Select
      value={value || ""}
      onChange={(event) => handleChange(event.target.value, inputIndex)}
    >
      <MenuItem value={null}>
        <em>None</em>
      </MenuItem>
      {columnNames.map((column, index) => (
        <MenuItem
          key={index}
          value={column}
          className={mappedColumns.has(column) ? "is-mapped" : ""}
        >
          {column}
        </MenuItem>
      ))}
    </Select>
  );
};
ColumnSelector.propTypes = {
  inputIndex: PropTypes.number,
  handleChange: PropTypes.func,
  value: PropTypes.string,
};

export const ColumnTextInput = ({ inputIndex, handleChange, value }) => {
  const { affiliateFileData, templateData } = useUpdateDataContext();
  const mappedColumns = getMappedColumns(templateData.template);
  const columnNames = affiliateFileData.columns;

  return (
    <Autocomplete
      autoSelect
      clearOnBlur
      handleHomeEndKeys
      value={value || null}
      onChange={(event, newValue) => handleChange(newValue, inputIndex)}
      freeSolo
      disablePortal
      options={columnNames}
      size="small"
      sx={{ width: 300 }}
      renderOption={(props, option) => {
        const selectedColor = "var(--seiu-blue)";
        const isMapped = mappedColumns.has(option) ? "is-mapped" : null;
        return (
          <>
            <Box
              component="li"
              {...props}
              className={`${props.className} ${isMapped}`}
              sx={{
                color: props["aria-selected"] ? selectedColor : null,
              }}
            >
              {option}
            </Box>
          </>
        );
      }}
      renderInput={(params) => {
        const { InputProps } = params;
        const isTextInput = !columnNames.includes(value)
          ? "is-text-input"
          : null;
        return (
          <TextField
            {...params}
            InputProps={{
              ...InputProps,
              className: isTextInput,
            }}
          />
        );
      }}
    />
  );
};
ColumnTextInput.propTypes = {
  inputIndex: PropTypes.number,
  handleChange: PropTypes.func,
  value: PropTypes.string,
};
